import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Badge,
  Container,
  Row,
  Col,
  Form,
  Table,
  Pagination,
  Spinner,
  Alert,
  Button,
  Modal,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import {
  productCategoryOptions,
  type CategoryCode,
} from '../components/products/productFormTypes';
import ProductPlanLimitModal from '../components/products/ProductPlanLimitModal';
import { supabase } from '../lib/supabase';
import type { UserOrganisation } from '../types/types';
import { getNumericPlanFeature } from '../util/planFeatures';

type ProductSortKey = 'name' | 'sku_model' | 'brand_name' | 'updated_at';
type ProductSortDirection = 'asc' | 'desc';
type ProductStatusFilter =
  | 'ALL'
  | 'PUBLISHED'
  | 'UNPUBLISHED'
  | 'WITHDRAWN'
  | 'ARCHIVED';

type ProductCategory = {
  name?: string | null;
  code?: string | null;
} | null;

type LifecycleAction = 'DISCONTINUE' | 'WITHDRAW' | 'PUBLISH';

type ProductPassportStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED' | 'WITHDRAWN';

type ProductPassportRecord = {
  status?: ProductPassportStatus | null;
  published_at?: string | null;
};

type ProductPassport = ProductPassportRecord | null;

type ProductAccessRequest = {
  id: string;
  status?: string | null;
};

type ProductRecord = {
  id: string;
  supplier_id: string;
  name: string;
  sku_model: string;
  gtin_ean: string | null;
  brand_name: string;
  updated_at: string;
  product_categories?: ProductCategory | ProductCategory[];
  product_passports?: ProductPassport | ProductPassport[];
  dpp_access_requests?: ProductAccessRequest[] | null;
};

function useQueryParams() {
  return new URLSearchParams(useLocation().search);
}

function getFirstRelation<T>(value: T | T[] | null | undefined): T | null {
  if (!value) return null;
  return Array.isArray(value) ? (value[0] ?? null) : value;
}

function isInteractiveTarget(target: EventTarget | null) {
  return (
    target instanceof HTMLElement &&
    Boolean(
      target.closest(
        'button, a, input, select, textarea, label, [data-row-action="true"]',
      ),
    )
  );
}

function getPassportList(
  value: ProductPassport | ProductPassport[] | null | undefined,
) {
  if (!value) return [] as ProductPassportRecord[];
  return (Array.isArray(value) ? value : [value]).filter(
    (passport): passport is ProductPassportRecord => Boolean(passport),
  );
}

function getPassportStatusPriority(status?: ProductPassportStatus | null) {
  switch (status) {
    case 'PUBLISHED':
      return 3;
    case 'ARCHIVED':
      return 2;
    case 'WITHDRAWN':
      return 1;
    default:
      return 0;
  }
}

function getProductLifecycleState(
  value: ProductPassport | ProductPassport[] | null | undefined,
) {
  const passports: ProductPassportRecord[] = getPassportList(value);
  const sortedPassports: ProductPassportRecord[] = [...passports].sort(
    (left: ProductPassportRecord, right: ProductPassportRecord) => {
      const rightTime = right.published_at
        ? new Date(right.published_at).getTime()
        : -1;
      const leftTime = left.published_at
        ? new Date(left.published_at).getTime()
        : -1;

      if (rightTime !== leftTime) {
        return rightTime - leftTime;
      }

      return (
        getPassportStatusPriority(right.status) -
        getPassportStatusPriority(left.status)
      );
    },
  );

  const latestNonDraftPassport =
    sortedPassports.find(
      (passport: ProductPassportRecord) => passport.status !== 'DRAFT',
    ) ?? null;
  const currentStatus = latestNonDraftPassport?.status ?? 'DRAFT';

  return {
    passports: sortedPassports,
    hasHistoricalPassport: Boolean(latestNonDraftPassport),
    currentStatus,
  };
}

function getProductStatusBadgeVariant(status: string) {
  switch (status) {
    case 'PUBLISHED':
      return 'success';
    case 'ARCHIVED':
      return 'warning';
    case 'WITHDRAWN':
      return 'danger';
    default:
      return 'secondary';
  }
}

function getProductStatusLabel(status: string) {
  switch (status) {
    case 'ARCHIVED':
      return 'Archived';
    case 'WITHDRAWN':
      return 'Withdrawn';
    case 'PUBLISHED':
      return 'Published';
    default:
      return 'Unpublished';
  }
}

export default function Products() {
  const navigate = useNavigate();
  const location = useLocation();
  const query = useQueryParams();

  const selectedOrganisation = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.organisation.selected,
  ) as UserOrganisation | undefined;

  const [search, setSearch] = useState(query.get('search') || '');
  const [page, setPage] = useState(Number(query.get('page') || 1));
  const [sortBy, setSortBy] = useState<ProductSortKey>(
    (query.get('sortBy') as ProductSortKey) || 'updated_at',
  );
  const [sortDirection, setSortDirection] = useState<ProductSortDirection>(
    (query.get('sortDirection') as ProductSortDirection) || 'desc',
  );
  const [statusFilter, setStatusFilter] = useState<ProductStatusFilter>(
    (query.get('status') as ProductStatusFilter) || 'ALL',
  );
  const [categoryFilter, setCategoryFilter] = useState<CategoryCode | 'ALL'>(
    (query.get('category') as CategoryCode | 'ALL') || 'ALL',
  );
  const [debouncedSearch, setDebouncedSearch] = useState(search);

  const [products, setProducts] = useState<ProductRecord[]>([]);
  const [productCount, setProductCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showPlanLimitModal, setShowPlanLimitModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ProductRecord | null>(null);
  const [deleteAction, setDeleteAction] =
    useState<LifecycleAction>('DISCONTINUE');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSuccess, setDeleteSuccess] = useState<string | null>(null);
  const [deletingProduct, setDeletingProduct] = useState(false);

  const pageSize = 10;
  const productLimit = getNumericPlanFeature(
    selectedOrganisation?.organisation?.plan,
    'dppProducts',
  );
  const hasReachedProductLimit =
    productLimit != null && productCount >= productLimit;

  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (page > 1) params.set('page', String(page));
    params.set('sortBy', sortBy);
    params.set('sortDirection', sortDirection);
    if (statusFilter !== 'ALL') params.set('status', statusFilter);
    if (categoryFilter !== 'ALL') params.set('category', categoryFilter);

    navigate(
      {
        pathname: location.pathname,
        search: params.toString(),
      },
      { replace: true },
    );
  }, [
    categoryFilter,
    location.pathname,
    navigate,
    page,
    search,
    sortBy,
    sortDirection,
    statusFilter,
  ]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      if (search) setPage(1);
    }, 400);

    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    const fetchProducts = async () => {
      const organisationId = selectedOrganisation?.organisation?.id;

      if (!organisationId) {
        setProducts([]);
        setProductCount(0);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const totalCountPromise = supabase
          .from('products')
          .select('id', { count: 'exact', head: true })
          .eq('supplier_id', organisationId);

        let productsQuery = supabase
          .from('products')
          .select(
            `
              id,
              supplier_id,
              name,
              sku_model,
              gtin_ean,
              brand_name,
              updated_at,
              product_categories(name, code),
              product_passports(status, published_at),
              dpp_access_requests(id, status)
            `,
            { count: 'exact' },
          )
          .eq('supplier_id', organisationId)
          .order(sortBy, { ascending: sortDirection === 'asc' });

        if (debouncedSearch) {
          productsQuery = productsQuery.or(
            `name.ilike.%${debouncedSearch}%,sku_model.ilike.%${debouncedSearch}%,brand_name.ilike.%${debouncedSearch}%,gtin_ean.ilike.%${debouncedSearch}%`,
          );
        }

        const [
          { data, error: productsError },
          { count, error: productCountError },
        ] = await Promise.all([productsQuery, totalCountPromise]);

        if (productsError) throw productsError;
        if (productCountError) throw productCountError;

        setProducts((data ?? []) as ProductRecord[]);
        setProductCount(count ?? 0);
      } catch (fetchError) {
        const errorMessage =
          fetchError instanceof Error
            ? fetchError.message
            : 'Failed to load products.';

        setError(errorMessage);
        setProducts([]);
        setProductCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [
    debouncedSearch,
    page,
    refreshKey,
    selectedOrganisation?.organisation?.id,
    sortBy,
    sortDirection,
  ]);

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const category = getFirstRelation(product.product_categories);
      const lifecycleState = getProductLifecycleState(
        product.product_passports,
      );
      const statusMatches =
        statusFilter === 'ALL'
          ? true
          : statusFilter === 'UNPUBLISHED'
            ? lifecycleState.currentStatus === 'DRAFT'
            : lifecycleState.currentStatus === statusFilter;
      const categoryMatches =
        categoryFilter === 'ALL' ? true : category?.code === categoryFilter;

      return statusMatches && categoryMatches;
    });
  }, [categoryFilter, products, statusFilter]);

  const filteredTotal = filteredProducts.length;
  const totalPages = Math.ceil(filteredTotal / pageSize);
  const currentPageProducts = filteredProducts.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );
  const hasActiveFilters =
    statusFilter !== 'ALL' ||
    categoryFilter !== 'ALL' ||
    Boolean(search.trim());
  const showNoProductsNotice =
    !loading && !error && products.length === 0 && !hasActiveFilters;
  const showNoMatchesNotice =
    !loading &&
    !error &&
    currentPageProducts.length === 0 &&
    !showNoProductsNotice;

  useEffect(() => {
    if (page > Math.max(totalPages, 1)) {
      setPage(1);
    }
  }, [page, totalPages]);

  const handleSort = (column: ProductSortKey) => {
    if (sortBy === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
    setPage(1);
  };

  const renderSortIcon = (column: ProductSortKey) => {
    if (sortBy !== column) return null;
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  const formatDateTime = (value?: string) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString();
  };

  const navigateToProduct = (productId: string) => {
    navigate(`/products/${productId}`);
  };

  const handleOpenCreateProduct = () => {
    if (hasReachedProductLimit) {
      setShowPlanLimitModal(true);
      return;
    }

    navigate('/products/new');
  };

  const openDeleteModal = (product: ProductRecord) => {
    const lifecycleState = getProductLifecycleState(product.product_passports);

    setDeleteTarget(product);
    setDeleteAction(
      lifecycleState.currentStatus === 'ARCHIVED'
        ? 'PUBLISH'
        : lifecycleState.currentStatus === 'WITHDRAWN'
          ? 'WITHDRAW'
          : 'DISCONTINUE',
    );
    setDeleteReason('');
    setDeleteError(null);
  };

  const closeDeleteModal = () => {
    if (deletingProduct) return;
    setDeleteTarget(null);
    setDeleteReason('');
    setDeleteError(null);
  };

  const handleDeleteProduct = async () => {
    if (!deleteTarget) return;

    const lifecycleState = getProductLifecycleState(
      deleteTarget.product_passports,
    );

    const isArchivedProduct = lifecycleState.currentStatus === 'ARCHIVED';
    const requiresReason =
      lifecycleState.hasHistoricalPassport && deleteAction !== 'PUBLISH';

    if (requiresReason && !deleteReason.trim()) {
      setDeleteError(
        'A lifecycle reason is required for archived or withdrawn products.',
      );
      return;
    }

    setDeletingProduct(true);
    setDeleteError(null);
    setDeleteSuccess(null);

    try {
      if (isArchivedProduct && deleteAction === 'PUBLISH') {
        const { data, error: publishError } = await supabase.functions.invoke(
          'publish-product-passport',
          {
            body: {
              product_id: deleteTarget.id,
            },
          },
        );

        if (publishError) {
          throw publishError;
        }

        if (!data?.success) {
          throw new Error('Product publish failed.');
        }

        setDeleteSuccess(`${deleteTarget.name} was published again.`);
      } else {
        const { data, error: invokeError } = await supabase.functions.invoke(
          'delete-product',
          {
            body: {
              product_id: deleteTarget.id,
              action: lifecycleState.hasHistoricalPassport
                ? deleteAction
                : null,
              reason: requiresReason ? deleteReason.trim() : null,
            },
          },
        );

        if (invokeError) {
          throw invokeError;
        }

        if (!data?.success) {
          throw new Error('Product lifecycle update failed.');
        }

        setDeleteSuccess(
          data.action === 'HARD_DELETED'
            ? `${deleteTarget.name} was permanently deleted.`
            : `${deleteTarget.name} was updated to ${String(data.newStatus ?? deleteAction).toLowerCase()}.`,
        );
      }

      closeDeleteModal();
      setRefreshKey((current) => current + 1);
    } catch (deleteProductError) {
      setDeleteError(
        deleteProductError instanceof Error
          ? deleteProductError.message
          : 'Unable to delete or update this product.',
      );
    } finally {
      setDeletingProduct(false);
    }
  };

  if (!selectedOrganisation?.organisation?.id) {
    return <div>Please select an organisation to view products.</div>;
  }

  const deleteLifecycleState = deleteTarget
    ? getProductLifecycleState(deleteTarget.product_passports)
    : null;
  const isArchivedDeleteTarget =
    deleteLifecycleState?.currentStatus === 'ARCHIVED';
  const requiresDeleteReason =
    Boolean(deleteLifecycleState?.hasHistoricalPassport) &&
    deleteAction !== 'PUBLISH';

  return (
    <Container fluid>
      <style>
        {`
          .product-list-table > :not(caption) > * > * {
            padding-top: 0.9rem;
            padding-bottom: 0.9rem;
            vertical-align: middle;
          }

          .product-list-table > tbody > tr > td:not(:first-child),
          .product-list-table > thead > tr > th:not(:first-child) {
            border-left-width: 0;
          }

          .product-list-table > tbody > tr > td:not(:last-child),
          .product-list-table > thead > tr > th:not(:last-child) {
            border-right-width: 0;
          }

          .product-list-subtext {
            font-size: 0.85rem;
            color: #6c757d;
            line-height: 1.3;
          }

          .product-list-category {
            font-size: 0.78rem;
            font-weight: 500;
          }

          .product-list-row {
            cursor: pointer;
          }

          .product-list-row:focus-visible {
            outline: 2px solid #0d6efd;
            outline-offset: -2px;
          }
        `}
      </style>

      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div>
          <h2 className="mb-1">Products</h2>
          <div className="text-muted">
            Manage your products and their associated Digital Product Passports.
          </div>
        </div>
        {!loading && (
          <div className="d-flex align-items-center gap-3">
            <div className="d-flex align-items-center gap-2 text-muted small">
              <span>
                {productLimit != null
                  ? `${productCount}/${productLimit} products`
                  : `${filteredTotal} ${filteredTotal === 1 ? 'product' : 'products'}`}
              </span>
              {productLimit != null ? (
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    <Tooltip id="products-plan-limit-tooltip">
                      {`You can create up to ${productLimit} products in your current plan. If you would like to add more products, consider upgrading your plan.`}
                    </Tooltip>
                  }
                >
                  <span
                    className="d-inline-flex align-items-center justify-content-center "
                    style={{ width: '1rem', height: '1rem' }}
                    aria-label="Products plan limit information"
                    tabIndex={0}
                  >
                    <i className="bi bi-info-circle-fill" />
                  </span>
                </OverlayTrigger>
              ) : null}
            </div>
            <Button variant="primary" onClick={handleOpenCreateProduct}>
              <i className="bi bi-plus-lg me-2" />
              Add New Product
            </Button>
          </div>
        )}
      </div>

      {error && !loading && (
        <Alert variant="danger" className="mt-3">
          Error loading products: {error}
        </Alert>
      )}

      {deleteSuccess && !loading && (
        <Alert
          variant="success"
          className="mt-3"
          dismissible
          onClose={() => setDeleteSuccess(null)}
        >
          {deleteSuccess}
        </Alert>
      )}

      <div
        className={`border bg-white p-3 ${
          currentPageProducts.length > 0 && !loading
            ? 'border-bottom-0 rounded-top-3 rounded-bottom-0'
            : 'rounded-3'
        }`}
      >
        <Row className="g-3 align-items-end">
          <Col md={4}>
            <Form.Group className="mb-0" controlId="productsStatusFilter">
              <Form.Label className="small text-muted mb-1">Status</Form.Label>
              <Form.Select
                value={statusFilter}
                onChange={(event) => {
                  setStatusFilter(event.target.value as ProductStatusFilter);
                  setPage(1);
                }}
              >
                <option value="ALL">All</option>
                <option value="PUBLISHED">Published</option>
                <option value="UNPUBLISHED">Unpublished</option>
                <option value="WITHDRAWN">Withdrawn</option>
                <option value="ARCHIVED">Archived</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-0" controlId="productsCategoryFilter">
              <Form.Label className="small text-muted mb-1">
                Category
              </Form.Label>
              <Form.Select
                value={categoryFilter}
                onChange={(event) => {
                  setCategoryFilter(event.target.value as CategoryCode | 'ALL');
                  setPage(1);
                }}
              >
                <option value="ALL">All</option>
                {productCategoryOptions.map((category) => (
                  <option key={category.code} value={category.code}>
                    {category.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-0" controlId="productsSearch">
              <Form.Label className="small text-muted mb-1">Search</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search by product name, SKU, brand, GTIN/EAN"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
      </div>

      {loading && (
        <div className="border border-top-0 bg-white p-4 rounded-bottom-3 d-flex justify-content-center">
          <Spinner animation="border" variant="primary" />
        </div>
      )}

      {showNoProductsNotice && (
        <div className="border border-top-0 bg-white p-4 rounded-bottom-3 text-center">
          <p className="text-muted mb-3">
            You have not created any products. To publish DPPs start with
            creating a new product.
          </p>
          <Button variant="primary" onClick={handleOpenCreateProduct}>
            Create Product
          </Button>
        </div>
      )}

      {showNoMatchesNotice && (
        <div className="border border-top-0 bg-white p-4 rounded-bottom-3 text-center text-muted">
          No products match your current search or filters.
        </div>
      )}

      {!loading && !error && currentPageProducts.length > 0 && (
        <>
          <Table bordered hover responsive className="mb-0 product-list-table">
            <thead>
              <tr>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('name')}
                >
                  Product {renderSortIcon('name')}
                </th>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('brand_name')}
                >
                  Brand {renderSortIcon('brand_name')}
                </th>
                <th>Category</th>
                <th>Status</th>
                <th>GTIN/EAN</th>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('updated_at')}
                >
                  Updated {renderSortIcon('updated_at')}
                </th>
                <th style={{ width: '1%' }}></th>
              </tr>
            </thead>
            <tbody>
              {currentPageProducts.map((product) => {
                const category = getFirstRelation(product.product_categories);
                const lifecycleState = getProductLifecycleState(
                  product.product_passports,
                );
                const badgeVariant = getProductStatusBadgeVariant(
                  lifecycleState.currentStatus,
                );
                const statusLabel = getProductStatusLabel(
                  lifecycleState.currentStatus,
                );
                const hasPendingAccessRequests = (
                  product.dpp_access_requests ?? []
                ).some((request) => request.status === 'PENDING');

                return (
                  <tr
                    key={product.id}
                    className="product-list-row"
                    role="button"
                    tabIndex={0}
                    onClick={(event) => {
                      if (isInteractiveTarget(event.target)) return;
                      navigateToProduct(product.id);
                    }}
                    onKeyDown={(event) => {
                      if (isInteractiveTarget(event.target)) return;
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        navigateToProduct(product.id);
                      }
                    }}
                  >
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <span className="fw-semibold">{product.name}</span>
                        {hasPendingAccessRequests && (
                          <span
                            className="rounded-circle bg-danger"
                            style={{
                              width: '0.6rem',
                              height: '0.6rem',
                              display: 'inline-block',
                              flexShrink: 0,
                            }}
                            aria-label="Access request exists"
                            title="Access request exists"
                          />
                        )}
                      </div>
                      <div className="product-list-subtext">
                        SKU/Model: {product.sku_model}
                      </div>
                    </td>
                    <td>{product.brand_name}</td>
                    <td>
                      {category?.name || category?.code ? (
                        <span className="badge bg-light text-dark border product-list-category">
                          {category?.name || category?.code}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      <Badge bg={badgeVariant} pill>
                        {statusLabel}
                      </Badge>
                    </td>
                    <td>{product.gtin_ean || '-'}</td>
                    <td>{formatDateTime(product.updated_at)}</td>
                    <td>
                      {lifecycleState.currentStatus === 'DRAFT' ? (
                        <Button
                          size="sm"
                          variant="link"
                          data-row-action="true"
                          onClick={(event) => {
                            event.stopPropagation();
                            openDeleteModal(product);
                          }}
                        >
                          <i className="bi bi-trash me-1" />
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <div
              className="border rounded-bottom-3 rounded-top-0 bg-white p-3"
              style={{ borderTop: 0 }}
            >
              <Pagination className="justify-content-center mb-0">
                <Pagination.First
                  disabled={page === 1}
                  onClick={() => setPage(1)}
                />
                <Pagination.Prev
                  disabled={page === 1}
                  onClick={() =>
                    setPage((currentPage) => Math.max(currentPage - 1, 1))
                  }
                />
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  return (
                    <Pagination.Item
                      key={pageNumber}
                      active={pageNumber === page}
                      onClick={() => setPage(pageNumber)}
                    >
                      {pageNumber}
                    </Pagination.Item>
                  );
                })}
                <Pagination.Next
                  disabled={page === totalPages}
                  onClick={() =>
                    setPage((currentPage) =>
                      Math.min(currentPage + 1, totalPages),
                    )
                  }
                />
                <Pagination.Last
                  disabled={page === totalPages}
                  onClick={() => setPage(totalPages)}
                />
              </Pagination>
            </div>
          )}
        </>
      )}

      <Modal show={Boolean(deleteTarget)} onHide={closeDeleteModal} centered>
        <Modal.Header closeButton={!deletingProduct}>
          <Modal.Title>
            {deleteLifecycleState?.hasHistoricalPassport
              ? 'Update Product Lifecycle'
              : 'Delete Product'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteTarget && deleteLifecycleState?.hasHistoricalPassport ? (
            <>
              <Alert
                variant={
                  deleteLifecycleState.currentStatus === 'WITHDRAWN'
                    ? 'danger'
                    : 'warning'
                }
              >
                Hard delete is blocked because this product already has a public
                passport history. Update the passport lifecycle instead to
                preserve traceability.
              </Alert>

              <p className="mb-3">
                <strong>{deleteTarget.name}</strong> currently has status{' '}
                <strong>
                  {getProductStatusLabel(deleteLifecycleState.currentStatus)}
                </strong>
                .
              </p>

              <Form.Group className="mb-3">
                <Form.Label>Lifecycle Action</Form.Label>
                <div className="d-flex flex-column gap-2">
                  {isArchivedDeleteTarget ? (
                    <>
                      <Form.Check
                        type="radio"
                        name="productLifecycleAction"
                        id="productLifecyclePublish"
                        checked={deleteAction === 'PUBLISH'}
                        onChange={() => setDeleteAction('PUBLISH')}
                        label="Publish product again and make the passport public"
                      />
                      <Form.Check
                        type="radio"
                        name="productLifecycleAction"
                        id="productLifecycleWithdraw"
                        checked={deleteAction === 'WITHDRAW'}
                        onChange={() => setDeleteAction('WITHDRAW')}
                        label="Withdraw product and replace public specifications with a revocation notice"
                      />
                    </>
                  ) : (
                    <>
                      <Form.Check
                        type="radio"
                        name="productLifecycleAction"
                        id="productLifecycleDiscontinue"
                        checked={deleteAction === 'DISCONTINUE'}
                        onChange={() => setDeleteAction('DISCONTINUE')}
                        label="Discontinue product and archive published passports"
                      />
                      <Form.Check
                        type="radio"
                        name="productLifecycleAction"
                        id="productLifecycleWithdraw"
                        checked={deleteAction === 'WITHDRAW'}
                        onChange={() => setDeleteAction('WITHDRAW')}
                        label="Withdraw product and replace public specifications with a revocation notice"
                      />
                    </>
                  )}
                </div>
              </Form.Group>

              {requiresDeleteReason ? (
                <Form.Group>
                  <Form.Label>Reason</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={deleteReason}
                    onChange={(event) => setDeleteReason(event.target.value)}
                    placeholder={
                      deleteAction === 'WITHDRAW'
                        ? 'Explain why this product is being withdrawn.'
                        : 'Explain why this product is being discontinued.'
                    }
                  />
                </Form.Group>
              ) : (
                <Alert variant="info" className="mb-0">
                  Publishing will reactivate the current archived passport and
                  make it publicly available again.
                </Alert>
              )}
            </>
          ) : deleteTarget ? (
            <p className="mb-0">
              Permanently delete <strong>{deleteTarget.name}</strong>? Draft
              passports will be removed automatically through the existing
              cascade on the product record.
            </p>
          ) : null}

          {deleteError && (
            <Alert variant="danger" className="mt-3 mb-0">
              {deleteError}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={closeDeleteModal}
            disabled={deletingProduct}
          >
            Cancel
          </Button>
          <Button
            variant={
              deleteLifecycleState?.hasHistoricalPassport
                ? deleteAction === 'WITHDRAW'
                  ? 'danger'
                  : 'warning'
                : 'danger'
            }
            onClick={() => void handleDeleteProduct()}
            disabled={deletingProduct}
          >
            {deletingProduct ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Working...
              </>
            ) : deleteLifecycleState?.hasHistoricalPassport ? (
              deleteAction === 'PUBLISH' ? (
                'Publish Product'
              ) : deleteAction === 'WITHDRAW' ? (
                'Withdraw Product'
              ) : (
                'Discontinue Product'
              )
            ) : (
              'Delete Product'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <ProductPlanLimitModal
        show={showPlanLimitModal}
        onHide={() => setShowPlanLimitModal(false)}
        onUpgrade={() => navigate('/settings?tab=organisation')}
      />
    </Container>
  );
}
